import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { fetchCbtAllLocations } from '@api/fetchers/cbt';
import { useEffect, useMemo, useState } from 'react';
import CbtSearchableSelect from './CbtSearchableSelect';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  width: 100%;
`;

const FieldLabel = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  color: var(--color-text-primary);

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: ${Spacings.SPACING_4B};
  }
`;

const HelperText = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;

const ProceedButton = styled(Button)`
  min-width: 251px;
  height: 48px;
  border: none;
  &:disabled {
    background-color: var(--color-base-light-0);
    color: var(--color-text-secondary);
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    min-width: unset;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;

  ${mediaQuery.uptoMobile} {
    justify-content: stretch;
    position: sticky;
    bottom: 0;
    background-color: var(--color-base-1);
    padding-top: ${Spacings.SPACING_4B};
    margin-top: auto;
  }
`;

const PreferredLocationSelectionForm = ({
  onSubmit,
  onLocationChange,
  isMobile = false,
  hideSubmitButton = false
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [locations, setLocations] = useState({});
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [isLocationsError, setIsLocationsError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsLocationsLoading(true);
    setIsLocationsError(false);
    setLocations({});

    fetchCbtAllLocations()
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setIsLocationsError(true);
          return;
        }
        setLocations(response?.locations ?? response?.data?.locations ?? {});
      })
      .catch(() => {
        if (cancelled) return;
        setIsLocationsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLocationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const stateOptions = useMemo(
    () =>
      Object.keys(locations).map((state) => ({
        label: state,
        value: state
      })),
    [locations]
  );

  const cityOptions = useMemo(() => {
    if (!selectedState) return [];
    return (locations[selectedState.value] ?? []).map((city) => ({
      label: city,
      value: city
    }));
  }, [locations, selectedState]);

  const canSubmit = Boolean(selectedState && selectedCity);

  const handleStateSelect = (option) => {
    setSelectedState(option);
    setSelectedCity(null);
    onLocationChange?.(null);
  };

  const handleCitySelect = (option) => {
    setSelectedCity(option);
    if (selectedState) {
      onLocationChange?.({
        preferredState: selectedState.value,
        preferredCity: option.value
      });
    }
  };

  const handleSubmit = async () => {
    if (
      !canSubmit ||
      !selectedState ||
      !selectedCity ||
      isSubmitting ||
      hasSubmitted
    )
      return;

    const locationSelection = {
      preferredState: selectedState.value,
      preferredCity: selectedCity.value
    };

    setIsSubmitting(true);
    try {
      const result = await onSubmit?.(locationSelection);
      if (result !== false) setHasSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormWrapper>
      <FieldGroup>
        <FieldLabel variant="p3">Choice of state</FieldLabel>
        <CbtSearchableSelect
          options={stateOptions}
          selectedOption={selectedState}
          onSelect={handleStateSelect}
          placeholder="Search for state"
          disabled={isLocationsLoading || !stateOptions.length}
          isOpen={openDropdown === 'state'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'state' : null)}
        />
        {isLocationsError && (
          <HelperText variant="p3">
            Unable to load locations. Please go back and try again.
          </HelperText>
        )}
        {!isLocationsLoading && !isLocationsError && !stateOptions.length && (
          <HelperText variant="p3">No locations available.</HelperText>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel variant="p3">Choice of city</FieldLabel>
        <CbtSearchableSelect
          options={cityOptions}
          selectedOption={selectedCity}
          onSelect={handleCitySelect}
          placeholder="Search for city"
          disabled={!selectedState}
          isOpen={openDropdown === 'city'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'city' : null)}
        />
      </FieldGroup>

      {!hideSubmitButton && !hasSubmitted && (
        <ActionRow>
          <ProceedButton
            label="Submit"
            variant="primary"
            size={isMobile ? 'large' : 'medium'}
            disabled={!canSubmit || isSubmitting}
            onClick={handleSubmit}
          />
        </ActionRow>
      )}
    </FormWrapper>
  );
};

export default PreferredLocationSelectionForm;
